'use client';

import { LoggedInUserProps } from '@interfaces/auth';
import { useThemeConfigStore } from '@store/theme-config-store';
import { v2Links } from '@utils/navigation/links';
import { Logo } from '@v2/ui/Logo';
import { typographyVariants } from '@v2/ui/Typography';
import { cn } from '@v2/utils/cn';
import { useSession } from 'next-auth/react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { CurrencyDropdown } from '../CurrencyPicker/CurrencyDropdown';
import { UserNav } from './UserNav';
import { ConcertsDropdown } from './components/ConcertsDropdown';
import { SportsDropdown } from './components/SportsDropdown/SportsDropdown';

const HeaderSearch = dynamic(
  () => import('../Search/HeaderSearch').then((module) => module.HeaderSearch),
  { ssr: false }
);
const WhiteLabelHeader = dynamic(() =>
  import('app/(white-label)/common/Header').then(
    (module) => module.WhiteLabelHeader
  )
);
// SSR the mobile chrome (hamburger + search). Drawer content stays light after dropdown splits.
const MobileMenu = dynamic(() =>
  import('./components/MobileMenu/MobileMenu').then(
    (module) => module.MobileMenu
  )
);

type HeaderBgVariant = 'white' | 'black';
export type HeaderProps = {
  user?: LoggedInUserProps | null;
  variant?: HeaderBgVariant;
  withSearchbar?: boolean;
  compactMobileSpacing?: boolean;
  children?: React.ReactNode;
};
export const Header = (props: HeaderProps) => {
  const isTenantEnabled = useThemeConfigStore((state) => state.isTenantEnabled);
  const { data: session } = useSession();

  const {
    user: serverUser,
    variant = 'white',
    withSearchbar = false,
    compactMobileSpacing = false,
  } = props;
  const user =
    serverUser === undefined
      ? ((session?.user as LoggedInUserProps | undefined) ?? null)
      : serverUser;

  const isBlackMode = variant === 'black';

  const buttonStyle = isBlackMode
    ? 'bg-white border border-gray-300 text-gray-800 '
    : 'bg-slate-500/25 border-transparent text-white';

  if (isTenantEnabled) return <WhiteLabelHeader {...props} user={user} />;
  return (
    <header
      className={cn(
        'relative z-10 mx-auto mt-0 flex w-full max-w-7xl items-center justify-between px-4 md:mb-20 md:h-24 xl:px-0',
        compactMobileSpacing ? 'mb-4 h-16' : 'mb-8 h-[72px]'
      )}
    >
      <div className="flex">
        <Link href={v2Links.home}>
          <Logo className="mr-8" variant={variant} />
        </Link>
        <div className="flex flex-row gap-x-5">
          {withSearchbar && (
            <div className="hidden lg:block">
              <HeaderSearch variant={variant} />
            </div>
          )}
          <nav
            className={`hidden font-semibold md:block ${isBlackMode ? 'text-gray-800' : 'text-white'}`}
          >
            <ul className="flex h-full items-center gap-3 text-center">
              <li className="inline">
                <SportsDropdown buttonStyle={buttonStyle} variant={variant} />
              </li>

              <li className="inline">
                <ConcertsDropdown />
              </li>
              <li className="inline w-16">
                <Link href="https://blog.elitesportstours.com/">Blog</Link>
              </li>
            </ul>
          </nav>
        </div>
      </div>
      <div
        className={`flex gap-4 ${isBlackMode ? 'text-gray-800' : 'text-white'}`}
      >
        <div className="hidden md:block">
          <CurrencyDropdown variant={variant} />
        </div>

        {!user && (
          <Link
            href={v2Links.login}
            className="hidden md:block"
            prefetch={false}
          >
            <button
              className={cn(
                `${typographyVariants({ size: 'tmd' })} h-12 w-24 rounded-lg ${buttonStyle} `
              )}
            >
              Sign in
            </button>
          </Link>
        )}

        <MobileMenu user={user} />

        {user && <UserNav user={user} variant={variant} />}
      </div>
    </header>
  );
};
